import { useState, useEffect, useCallback } from 'react';
import { Circle } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ToolHeader } from '@/components/common/ToolHeader';
import { WizardStepper } from '@/components/sector/WizardStepper';
import { TyreSelector } from './TyreSelector';
import { TyreHistoryTable } from './TyreHistoryTable';
import { TyreExpectationsTable } from './TyreExpectationsTable';
import type { AnalyzeTyreResponse, TyreSetInfo, TyreFastestLap, TyreTrackExpectation } from '@/types/telemetry';

interface TyreAnalysisProps {
  tyreId: string;
}

export function TyreAnalysis({ tyreId }: TyreAnalysisProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const wizardSteps = [t('tyre.wizardSelect'), t('tyre.wizardResults')];
  const currentStep = tyreId ? 1 : 0;

  const [tyreSet, setTyreSet] = useState<TyreSetInfo | null>(null);
  const [fastestLaps, setFastestLaps] = useState<TyreFastestLap[]>([]);
  const [totalLapsDriven, setTotalLapsDriven] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const [longestSessionLaps, setLongestSessionLaps] = useState(0);
  const [expectations, setExpectations] = useState<TyreTrackExpectation[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch tyre analysis data when tyreId is provided
  useEffect(() => {
    if (!tyreId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const resp = await rpc<AnalyzeTyreResponse>('AnalyzeTyre', { tyreId });
        if (!cancelled) {
          setTyreSet(resp.tyreSet);
          setFastestLaps(resp.fastestLaps || []);
          setTotalLapsDriven(resp.totalLapsDriven || 0);
          setTotalSessions(resp.totalSessions || 0);
          setLongestSessionLaps(resp.longestSessionLaps || 0);
          setExpectations(resp.expectations || []);
        }
      } catch (e) {
        console.error('Failed to load tyre analysis:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tyreId]);

  const handleSelect = useCallback((id: string) => {
    navigate(`#/analysis/tyre/${id}`);
  }, [navigate]);

  const handleStepClick = useCallback((targetStep: number) => {
    if (targetStep === 0) {
      navigate('#/analysis/tyre');
    }
  }, [navigate]);

  if (loading) return <LoadingSpinner />;

  // Step 0: Select tyre
  if (!tyreId) {
    return (
      <div>
        <ToolHeader title={t('analysis.tyre.title')} icon={Circle} />

        {/* Wizard stepper */}
        <div className="flex justify-center mb-6">
          <WizardStepper
            steps={wizardSteps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Select tyre content */}
        <div className="max-w-2xl mx-auto">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-text-primary">{t('tyre.selectTitle')}</h2>
            <p className="text-xs text-text-muted mt-0.5">{t('tyre.selectHint')}</p>
          </div>
          <TyreSelector onSelect={handleSelect} />
        </div>
      </div>
    );
  }

  // Step 1: Results
  const isDry = tyreSet?.compound.toLowerCase() === 'dry';
  const compoundLabel = isDry ? t('tyre.dry') : t('tyre.wet');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader
        title={t('analysis.tyre.title')}
        subtitle={tyreSet ? `${tyreSet.brand} ${tyreSet.type} #${tyreSet.nr}` : undefined}
        icon={Circle}
        exitHref="#/analysis/tyre"
        exitLabel={t('tyre.back')}
      />

      {/* Wizard stepper */}
      <div className="flex justify-center mb-6">
        <WizardStepper
          steps={wizardSteps}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />
      </div>

      <div className="flex flex-col gap-6">
        {/* Tyre info card */}
        {tyreSet && (
          <div className="card p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
                <Circle size={18} className="text-accent" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-text-primary">{tyreSet.brand} {tyreSet.type}</span>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-[0.68rem] font-medium border ${
                      isDry
                        ? 'bg-green/10 text-green border-green/20'
                        : 'bg-purple/10 text-purple border-purple/20'
                    }`}
                  >
                    {compoundLabel}
                  </span>
                </div>
                <span className="text-xs text-text-muted">#{tyreSet.nr}</span>
              </div>
            </div>

            {/* IDs grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              {[
                { label: 'FL', value: tyreSet.tyreIdFl },
                { label: 'FR', value: tyreSet.tyreIdFr },
                { label: 'RL', value: tyreSet.tyreIdRl },
                { label: 'RR', value: tyreSet.tyreIdRr },
              ].map(({ label, value }) => (
                <div key={label} className="bg-bg-surface rounded-md px-3 py-2 border border-border-subtle">
                  <div className="text-[0.68rem] text-text-muted font-medium mb-0.5">{label}</div>
                  <div className="mono text-xs text-text-secondary truncate">{value || '—'}</div>
                </div>
              ))}
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="card p-4 text-center">
                <div className="mono text-2xl font-bold text-text-primary">{totalLapsDriven}</div>
                <div className="text-xs text-text-muted mt-1">{t('tyre.totalLaps')}</div>
              </div>
              <div className="card p-4 text-center">
                <div className="mono text-2xl font-bold text-text-primary">{totalSessions}</div>
                <div className="text-xs text-text-muted mt-1">{t('tyre.totalSessions')}</div>
              </div>
              <div className="card p-4 text-center">
                <div className="mono text-2xl font-bold text-text-primary">{longestSessionLaps}</div>
                <div className="text-xs text-text-muted mt-1">{t('tyre.longestSession')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Fastest Laps */}
        <div>
          <h3 className="section-title mb-3">{t('tyre.fastestLaps')}</h3>
          <TyreHistoryTable laps={fastestLaps} />
        </div>

        {/* Expectations */}
        <div>
          <h3 className="section-title mb-3">{t('tyre.expectations')}</h3>
          <TyreExpectationsTable
            expectations={expectations}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}

